import React from 'react';
import PropTypes from 'prop-types';
import delve from 'dlv';

import { QueryComponent } from '../../GraphQL';
import { classroomTableOfContent } from '../../GraphQL/query/Classroom.query';

import { useClassroomContext } from '../../Context/ClassroomContext';

const getSubsectionsFromSections = (sections) => {
  const subsections = sections.map((section) => section.subSections).flat();
  return subsections;
};

/**
 * Check if the subsection is the first or last of the section
 */
const validateSubsectionOrderInSection = (sections, currentSubsectionId, variant) => {
  return (
    sections.findIndex((section) => {
      const lastSubsection = section.subSections[section.subSections.length - 1];
      const firstSubsection = section.subSections[0];
      switch (variant) {
        case 'FIRST':
          return currentSubsectionId === firstSubsection.id;
        case 'LAST':
        default:
          return currentSubsectionId === lastSubsection.id;
      }
    }) !== -1
  );
};

/**
 * This container component provides necessary functions/data for navigating among subsections in Classroom.
 * It needs to be used under ClassroomProvider.
 */
const SubsectionNavigation = ({ children }) => {
  const { updateCurrentSubsection, classroomCourseId, currentSubsectionId } = useClassroomContext();

  const goToSubsection = (id, referId) => {
    updateCurrentSubsection(id, referId);
  };
  return (
    <QueryComponent
      query={classroomTableOfContent}
      variables={{
        classroomCourseId,
      }}
    >
      {(data) => {
        const sections = delve(data, ['classroomCourse', 'tableOfContent', 'sections'], []);
        const subsections = getSubsectionsFromSections(sections);

        const currentSubsectionIndex = subsections.findIndex(
          ({ id }) => id === currentSubsectionId
        );

        /**
         * Specify whether if currentSubsection is the first or last in the course.
         */
        const isLastSubsectionInCourse = currentSubsectionIndex === subsections.length;
        const isFirstSubsectionInCourse = currentSubsectionIndex === 0;

        /**
         * Specify whether if currentSubsection is the first or last in any sections.
         */
        const isFirstSubsectionInSection = validateSubsectionOrderInSection(
          sections,
          currentSubsectionId,
          'FIRST'
        );
        const isLastSubsectionInSection = validateSubsectionOrderInSection(
          sections,
          currentSubsectionId,
          'LAST'
        );

        /**
         * An object contain data of next/previous subsection including
         * { id, referId, title, contentType }
         */
        const nextSubsection = !isLastSubsectionInCourse
          ? subsections[currentSubsectionIndex + 1]
          : null;
        const previousSubsection = !isFirstSubsectionInCourse
          ? subsections[currentSubsectionIndex - 1]
          : null;

        return children({
          isLastSubsectionInCourse,
          isLastSubsectionInSection,
          isFirstSubsectionInCourse,
          isFirstSubsectionInSection,
          goToNextSubsection: () => {
            goToSubsection(nextSubsection.id, nextSubsection.referId);
          },
          goToPreviousSubsection: () => {
            goToSubsection(previousSubsection.id, previousSubsection.referId);
          },
          goToSubsection,
          nextSubsection,
          previousSubsection,
        });
      }}
    </QueryComponent>
  );
};

SubsectionNavigation.propTypes = {
  children: PropTypes.func,
};

SubsectionNavigation.defaultProps = {
  children: () => {},
};

export default SubsectionNavigation;
